import type { ReactNode } from 'react';

/**
 * The standard marketing-page section wrapper (#3216, kit for #3122). One
 * source for the `<section>` + max-width container + inter-section divider that
 * HomePage, LeaderWhyPage and others each re-expressed by hand (gap pass:
 * `docs/canonical/audits/public-surface-unification-2026-08-27.md`).
 *
 * Extracted from HomePage's body sections. `overflow-x-clip` is always on: the
 * `Reveal` fly-ins start off to the side and would add a horizontal scrollbar
 * on narrow screens without it (see `Reveal`). On a section with no side-Reveal
 * (the close CTA) it is inert — nothing overflows — so the class is safe to
 * carry everywhere and removes a per-section decision.
 *
 * The hero is deliberately NOT a MarketingSection: it is full-bleed with a
 * background scrim and its own layout, and forcing it through here would mean a
 * prop for every one of its differences. Sections whose INTERNALS are
 * page-specific (the front door's product demo) keep their own markup as
 * children — this primitive owns the frame, not the contents.
 */
interface MarketingSectionProps {
  /** Anchor id on the `<section>` (e.g. the front door's `what-it-looks-like`). */
  id?: string;
  /** Render a `<hr>` divider immediately before the section. */
  divider?: boolean;
  /** Vertical rhythm: `default` (body sections) or `cta` (the closing band). */
  spacing?: 'default' | 'cta';
  /** Text alignment on the section (the closing band is centered). */
  align?: 'left' | 'center';
  children: ReactNode;
}

const SPACING_CLASS: Record<NonNullable<MarketingSectionProps['spacing']>, string> = {
  default: 'py-19',
  cta: 'py-24',
};

export default function MarketingSection({
  id,
  divider = false,
  spacing = 'default',
  align = 'left',
  children,
}: MarketingSectionProps) {
  const sectionClass = ['overflow-x-clip', SPACING_CLASS[spacing], align === 'center' ? 'text-center' : '']
    .filter(Boolean)
    .join(' ');

  return (
    <>
      {divider && <hr className="border-oro-border" />}
      <section id={id} className={sectionClass}>
        <div className="max-w-5xl mx-auto px-6">{children}</div>
      </section>
    </>
  );
}
